<template>
  <div class="analytics-page space-y-5 stagger-children">
    <!-- Breadcrumb + Page Header -->
    <div class="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
      <!-- shrink-0: the toolbar wraps before the breadcrumb or title does -->
      <div class="shrink-0 min-w-0">
        <nav class="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 mb-1 whitespace-nowrap" aria-label="Breadcrumb">
          <router-link
            :to="{ name: 'AnalyticsDashboard' }"
            class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium"
          >
            Analytics
          </router-link>
          <span class="material-icons text-[14px]" aria-hidden="true">chevron_right</span>
          <router-link
            :to="{ name: 'SupplierGroups' }"
            class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium"
          >
            Supplier Groups
          </router-link>
          <span class="material-icons text-[14px]" aria-hidden="true">chevron_right</span>
          <span class="text-neutral-700 dark:text-neutral-300 font-medium" aria-current="page">Group</span>
        </nav>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          {{ groupName }}
        </h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span class="font-medium text-neutral-700 dark:text-neutral-300">{{ periodLabel }}</span>
          <span aria-hidden="true">·</span>
          <span>vs previous period</span>
          <template v-if="groupAnalytics">
            <span aria-hidden="true">·</span>
            <span>{{ groupAnalytics.totalOrders?.toLocaleString() }} orders across {{ memberCount }} members</span>
          </template>
          <span v-else class="inline-block h-4 w-20 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse align-middle" aria-hidden="true"></span>
        </p>
      </div>

      <AnalyticsToolbar
        v-model:start-date="startDate"
        v-model:end-date="endDate"
        :max-date="maxDate"
        :active-toggle="activeToggle"
        :quick-toggles="quickToggles"
        :downloading="downloading"
        @apply-range="applyQuickRange"
        @download="handleDownload"
      />
    </div>

    <!-- Error Banner -->
    <AlertBanner v-if="error" type="error" :message="error" @dismiss="error = null">
      <template #action>
        <button
          @click="loadData"
          class="text-sm font-semibold text-red-600 dark:text-red-400 hover:underline"
        >
          Retry
        </button>
      </template>
    </AlertBanner>

    <!-- KPI Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label="Total Actual Volume"
        :value="groupAnalytics?.totalActualVolume ?? null"
        unit="L"
        icon="local_gas_station"
        color="primary"
        hero
        :loading="loading && !groupAnalytics"
        :delta="prevDelta('volumeChange', '%', 'volumePrev', ' L')"
        :sparkline="volSpark"
      />

      <KpiCard
        label="Completed Orders"
        :value="groupAnalytics?.completedOrders ?? null"
        :unit="`of ${formatNumber(groupAnalytics?.totalOrders)}`"
        icon="check_circle"
        color="emerald"
        :loading="loading && !groupAnalytics"
        :delta="prevDelta('ordersChange', '%', 'ordersPrev', ' orders')"
        :sparkline="completedSpark"
      />

      <!-- Replaces "Total Orders", which duplicated the denominator shown in the card
           beside it. How many members actually produced oil this period is the number
           a group manager cannot get anywhere else on the page. -->
      <KpiCard
        label="Active Members"
        :value="activeMembers"
        :unit="`of ${formatNumber(memberCount)}`"
        icon="storefront"
        color="secondary"
        :loading="loading && !groupAnalytics"
      />

      <KpiCard
        label="Completion Rate"
        :value="groupAnalytics?.completionRate ?? null"
        unit="%"
        icon="task_alt"
        color="amber"
        :decimals="1"
        :loading="loading && !groupAnalytics"
        :delta="prevDelta('completionRateChange', 'pp', 'completionRatePrev', '%')"
        :sparkline="rateSpark"
      />
    </div>

    <!-- Trend beside the member ranking — same row structure as the site dashboard.
         The ranked list replaces a horizontal bar chart that turned 50 members into
         1px lines with most labels dropped. -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
      <div class="lg:col-span-2">
        <ChartCard
          title="Volume Trend"
          subtitle="Actual litres collected per week, all members"
          fill
          :loading="loading"
          :is-empty="!hasTrend"
          empty-icon="show_chart"
          empty-text="No trend data available"
          aria-label="Weekly actual volume trend line chart"
        >
          <Line :data="trendData" :options="trendOptions" />
          <template v-if="partialWeeks.length" #footer>
            <span class="inline-flex items-center gap-1">
              <span class="inline-block w-4 border-t-2 border-dashed border-neutral-400 dark:border-neutral-500" aria-hidden="true"></span>
              * partial week — fewer than 7 days of the selected period fall in it ({{ partialWeekSummary }}); hover for details
            </span>
          </template>
        </ChartCard>
      </div>

      <RankedBarList
        title="Member Comparison"
        subtitle="Actual volume, ranked · click a member to drill down"
        :items="memberItems"
        :loading="loading"
        :max-visible="8"
        items-label="members"
        clickable
        empty-icon="leaderboard"
        empty-text="No member data"
        @select="handleMemberSelect"
      />
    </div>

    <!-- Supplier Breakdown Table -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
      <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Supplier Breakdown</h2>
      <DataTable
        :data="supplierTableData"
        :columns="supplierColumns"
        :loading="loading"
        :clickable="true"
        :pagination="true"
        :searchable="true"
        row-key="supplierId"
        loading-text="Loading supplier data..."
        empty-text="No supplier data available for this period"
        @row-click="handleSupplierClick"
      >
        <!-- Supplier Name -->
        <template #cell-supplierName="{ row }">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
              <span class="text-xs font-bold text-primary-700 dark:text-primary-300">
                {{ row.supplierName?.charAt(0)?.toUpperCase() }}
              </span>
            </div>
            <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {{ row.supplierName }}
            </span>
          </div>
        </template>

        <!-- Actual Volume -->
        <template #cell-actualVolume="{ value }">
          <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Orders -->
        <template #cell-orders="{ value }">
          <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Completed -->
        <template #cell-completed="{ value }">
          <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Rate -->
        <template #cell-rate="{ value }">
          <div class="flex items-center gap-2">
            <div class="flex-1 h-1.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden max-w-[60px]">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="value >= 90 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-500'"
                :style="{ width: (value || 0) + '%' }"
              ></div>
            </div>
            <!-- Non-color cue for the good/fair/low band (bar hue alone is invisible to colorblind users) -->
            <span class="sr-only">{{ value >= 90 ? 'Good' : value >= 70 ? 'Fair' : 'Low' }} completion:</span>
            <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {{ value != null ? value.toFixed(1) : '--' }}%
            </span>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useAnalytics } from '@/composables/useAnalytics'
import useToast from '@/composables/useToast'
import { useDateRange } from '@/composables/useDateRange'
import AnalyticsToolbar from '@/components/analytics/AnalyticsToolbar.vue'
import RankedBarList from '@/components/analytics/RankedBarList.vue'
import { useChartTheme } from '@/composables/useChartTheme'
import { useWeeklyTrend } from '@/composables/useWeeklyTrend'
import { formatNumber, formatPeriodLabel } from '@/utils/format'
import DataTable from '@/components/ui/DataTable.vue'
import KpiCard from '@/components/analytics/KpiCard.vue'
import ChartCard from '@/components/analytics/ChartCard.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip)

export default {
  name: 'SupplierGroupAnalytics',
  components: {
    AnalyticsToolbar,
    RankedBarList,
    DataTable,
    KpiCard,
    ChartCard,
    AlertBanner,
    Line,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { showToast } = useToast()
    const { groupAnalytics, loading, error, fetchGroupAnalytics, downloadReport } = useAnalytics()
    const { theme, chartColors } = useChartTheme()

    const loadData = async () => {
      await fetchGroupAnalytics(props.id, startDate.value, endDate.value)
    }

    // Keep the selected period in the URL (?start/&end) so the range survives
    // back-navigation and supplier drill-downs inherit it.
    const syncRangeToQuery = () => {
      router.replace({ query: { ...route.query, start: startDate.value, end: endDate.value } })
    }

    let skipNextRangeFetch = false
    function onRangeChange() {
      syncRangeToQuery()
      if (skipNextRangeFetch) {
        // Initial query-param adoption; onMounted performs the first fetch.
        skipNextRangeFetch = false
        return
      }
      loadData()
    }

    const { startDate, endDate, activeToggle, quickToggles, applyQuickRange, maxDate } =
      useDateRange(onRangeChange)

    if (
      route.query.start && route.query.end &&
      route.query.start <= route.query.end && // ignore an inverted/malformed range
      (route.query.start !== startDate.value || route.query.end !== endDate.value)
    ) {
      skipNextRangeFetch = true
      startDate.value = String(route.query.start)
      endDate.value = String(route.query.end)
    }

    onMounted(() => {
      store.commit('setCurrentRoute', 'Group Analytics')
      loadData()
    })

    // Derived group name
    const groupName = computed(() => {
      return groupAnalytics.value?.groupName || 'Group Analytics'
    })

    // Sparkline series from the weekly trends array (null if missing/short)
    const sparkFrom = (key) => {
      const t = groupAnalytics.value?.trends
      return t && t.length >= 2 ? t.map((w) => w[key] ?? 0) : null
    }
    const volSpark = computed(() => sparkFrom('actualVolume'))
    const completedSpark = computed(() => sparkFrom('completedCount'))
    const rateSpark = computed(() => sparkFrom('completionRate'))

    // Plain-language period for the header ("1 Jan – 2 Sep 2026")
    const periodLabel = computed(() => formatPeriodLabel(startDate.value, endDate.value))

    // Previous-period delta pill, with the previous value spelled out when the API sends it.
    const prevDelta = (changeKey, suffix, prevKey, prevUnit) => {
      const p = groupAnalytics.value?.prevPeriodComparison
      if (!p || p[changeKey] == null) return null
      const prev = p[prevKey]
      const prevText = prev == null ? null : (suffix === 'pp' ? `prev: ${Number(prev).toFixed(1)}${prevUnit}` : `prev: ${formatNumber(prev)}${prevUnit}`)
      return { value: p[changeKey], suffix, prevLabel: prevText }
    }

    // Members — API returns suppliers[] with totalActualVolume / totalOrders / completedOrders
    const members = computed(() => groupAnalytics.value?.suppliers || [])
    const memberCount = computed(() => members.value.length)
    const activeMembers = computed(() =>
      groupAnalytics.value ? members.value.filter((s) => (s.totalOrders ?? s.orders ?? 0) > 0).length : null
    )
    const memberItems = computed(() =>
      members.value.map((s) => ({
        id: s.supplierId || s.id,
        label: s.supplierName || s.name,
        value: s.totalActualVolume ?? s.actualVolume ?? 0,
        count: s.totalOrders ?? s.orders ?? 0,
      }))
    )
    const handleMemberSelect = (item) => {
      router.push({
        name: 'AnalyticsSupplierView',
        params: { id: item.id },
        query: { start: startDate.value, end: endDate.value },
      })
    }

    // Volume trend — shared weekly model (date labels, partial-week marking, brand colours)
    const weeklyRef = computed(() => groupAnalytics.value?.trends || [])
    const { hasTrend, trendData, trendOptions, partialWeeks, partialWeekSummary } =
      useWeeklyTrend(weeklyRef, startDate, endDate, theme, chartColors)

    // Supplier table columns
    const supplierColumns = [
      { key: 'supplierName', label: 'Supplier', sortable: true },
      { key: 'actualVolume', label: 'Actual Vol (L)', sortable: true, align: 'right' },
      { key: 'orders', label: 'Orders', sortable: true, align: 'right' },
      { key: 'completed', label: 'Completed', sortable: true, align: 'right' },
      { key: 'rate', label: 'Rate', sortable: true, align: 'right' },
    ]

    const supplierTableData = computed(() => {
      const suppliers = groupAnalytics.value?.suppliers
      if (!suppliers || !suppliers.length) return []
      return suppliers.map((s) => ({
        supplierId: s.supplierId || s.id,
        supplierName: s.supplierName || s.name,
        actualVolume: s.totalActualVolume ?? s.actualVolume ?? 0,
        orders: s.totalOrders ?? s.orders ?? 0,
        completed: s.completedOrders ?? s.completed ?? 0,
        rate: s.completionRate ?? (
          (s.totalOrders ?? s.orders)
            ? (((s.completedOrders ?? s.completed ?? 0) / (s.totalOrders ?? s.orders ?? 1)) * 100)
            : 0
        ),
      }))
    })

    const handleSupplierClick = (row) => {
      // Pass the period along so the drill-down compares the same range.
      router.push({
        name: 'AnalyticsSupplierView',
        params: { id: row.supplierId },
        query: { start: startDate.value, end: endDate.value },
      })
    }

    // Download handler
    const downloading = ref(null) // 'pdf' | 'csv' while a report generates server-side

    const handleDownload = async (format) => {
      if (downloading.value) return
      downloading.value = format
      error.value = null
      try {
        await downloadReport(store.state.currentSite, startDate.value, endDate.value, 'group', props.id, format)
        // downloadReport never throws — failures surface via the shared error ref.
        if (error.value) {
          error.value = null // the banner's Retry reloads page data, wrong for a download
          showToast('error', 'Failed to download report. Please try again.')
        } else {
          showToast('success', 'Report downloaded')
        }
      } finally {
        downloading.value = null
      }
    }

    return {
      // State
      startDate,
      endDate,
      activeToggle,
      quickToggles,
      maxDate,
      loading,
      error,
      groupAnalytics,
      groupName,
      periodLabel,
      prevDelta,
      // Sparklines
      volSpark,
      completedSpark,
      rateSpark,
      // Members + charts
      memberCount,
      activeMembers,
      memberItems,
      handleMemberSelect,
      hasTrend,
      trendData,
      trendOptions,
      partialWeeks,
      partialWeekSummary,
      // Supplier table
      supplierColumns,
      supplierTableData,
      // Methods
      loadData,
      applyQuickRange,
      handleSupplierClick,
      handleDownload,
      downloading,
      formatNumber,
    }
  },
}
</script>
